/**
 * Site view component.
 * @module components/View/SiteView
 */

import React from 'react';
import PropTypes from 'prop-types';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Image, Grid, Container } from 'semantic-ui-react';
import whatwedoImg from './images/robert-beard-danielle-roossien-at-workbench.webp';
import contactusImg from './images/robert-beard-danielle-roossien-workshop.webp';
import daanImg from './images/danielle-roossien-at-buffing-machine.webp';
import bobImg from './images/robert-beard-at-sanding-machine.webp';

import Header from './Header';
import ogImg from './images/handmade-leatherworking-tools-pro-series-tools.jpg';

import backgroundersImg from './images/backgrounder-leatherworking-tool.webp';
import basketweavesImg from './images/basketweave-leatherworking-tool.webp';
import bevelersImg from './images/beveler-leatherworking-tool.webp';
import bordersImg from './images/border-leatherworking-tool.webp';
import camouflageImg from './images/camouflage-leatherworking-tool.webp';
import figurecarvingImg from './images/figure-carving-leatherworking-tool.webp';
import flowersImg from './images/flower-leatherworking-tool.webp';
import geometricsImg from './images/geometric-leatherworking-tool.webp';
import mattersImg from './images/matter-leatherworking-tool.webp';
import mulefootsImg from './images/mulefoot-leatherworking-tool.webp';
import pearshadersImg from './images/pear-shader-leatherworking-tool.webp';
import seedersImg from './images/seeder-leatherworking-tool.webp';
import veinersImg from './images/veiner-leatherworking-tool.webp';
import modelingspoonsImg from './images/modeling-spoon-leatherworking-tool.webp';
import swivelknivesImg from './images/swivel-knive-leatherworking-tool.webp';

// JSON-LD structured data for the home page.
// TODO: replace with the real Facebook / Instagram profile URLs.
const SITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Pro Series Tools LLC',
  url: 'https://proseriesleathertools.com',
  description:
    'Handmade, high-quality leatherworking tools crafted by master artisans Robert Beard and Daniëlle Roossien.',
  telephone: '+1-505-632-7039',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Farmington',
    addressRegion: 'NM',
    addressCountry: 'US',
  },
  areaServed: [
    { '@type': 'Country', name: 'United States' },
    { '@type': 'AdministrativeArea', name: 'Worldwide' },
  ],
  founder: [
    {
      '@type': 'Person',
      name: 'Robert Beard',
      award: 'Al Stohlman Award for Achievement in Leathercraft',
    },
    {
      '@type': 'Person',
      name: 'Daniëlle Roossien',
      award:
        'Award for Endeavour / Best Achievement at the University of Lincoln',
      alumniOf: {
        '@type': 'CollegeOrUniversity',
        name: 'University of Lincoln',
      },
    },
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+1-505-632-7039',
      contactType: 'sales',
      availableLanguage: 'English',
    },
    {
      '@type': 'ContactPoint',
      email: 'info@proseriesleathertools.com',
      contactType: 'customer service',
      availableLanguage: 'English',
    },
  ],
  sameAs: [
    'https://www.facebook.com/proseriestools',
    'https://www.instagram.com/proseriestools',
  ],
};

/**
 * Site view component class.
 * @function SiteView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const SiteView = ({ content }) => {
  const ogTitle =
    content?.title || 'Handmade Leatherworking Tools | Pro Series Tools';
  const ogDescription =
    content?.description ||
    'Handmade, high-quality leatherworking tools crafted by master artisans Robert Beard and Dani\u00eblle Roossien.';
  const ogImage = `${ogImg}`;

  return (
    <div id="page-document" className="ui container">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(SITE_SCHEMA)}
        </script>
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <script
          defer
          src="https://proseriesleathertools.com/stats/script.js"
          data-website-id="61fd2e17-97e5-4383-92f5-2c3fc5f9d6bb"
        />
      </Helmet>
      <Header />

      <section className="full-width section" id="whatwedo">
        <Container>
          <h2 className="animation">What We Do</h2>
          <div className="header-line animation" />
          <p className="animation">
            We create high-quality, handmade leatherworking tools. Our mission
            is to provide artisans and hobbyists with the finest tools that
            enhance their craft and inspire creativity. Each tool is
            meticulously crafted by hand with attention to detail, ensuring
            durability and performance. We believe in the power of craftsmanship
            and the joy it brings to those who use our tools.
          </p>
          <br />
          <br />
          <br />
          <br />
        </Container>
        <div className="cover-container">
          <Image
            src={whatwedoImg}
            alt="Robert Beard and Daniëlle Roossien working at their workshop workbench."
            loading="lazy"
            className="cover"
          />
          <div className="cover-overlay" />
          <div className="cover-text">
            <Container>
              <p className="cover-title animation">
                Craftsmanship at its Finest
              </p>
              <div className="header-line animation" />
              <p className="animation">
                Our tools are designed for those who appreciate the art of
                craftsmanship. Whether you are a professional artisan or a
                hobbyist, our tools will elevate your work to new heights.
              </p>
            </Container>
          </div>
        </div>
      </section>

      <section className="full-width section" id="ourwork">
        <Container>
          <h2 className="animation">Our Work</h2>
          <div className="header-line animation" />
          <p className="animation">
            We take pride in our work, creating tools that are not only
            functional but also beautiful. Our tools are crafted with the finest
            materials and designed to meet the needs of our customers. With over
            2000 different tools available, we offer a wide range of options for
            every artisan. Contact us to learn more about our products and how
            we can help you with your next project.
          </p>
        </Container>
        <Container>
          <Grid stackable stretched columns={12} className="animation">
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={backgroundersImg}
                    alt="A detailed macro closeup of a backgrounder leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Backgrounders</h3>{' '}
                  <p>
                    are essential tools for creating intricate designs on
                    leather. They allow artisans to add depth and texture to
                    their work, enhancing the overall aesthetic of their
                    projects.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={basketweavesImg}
                    alt="A detailed macro closeup of a basketweave leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Basketweaves</h3>{' '}
                  <p>
                    are versatile tools used to create intricate patterns on
                    leather. They are perfect for adding texture and detail to
                    various leather projects, enhancing their visual appeal.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={bevelersImg}
                    alt="A detailed macro closeup of a beveler leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Bevelers</h3>{' '}
                  <p>
                    are essential tools for creating clean and precise edges on
                    the swivel knife cuts. They help artisans achieve a
                    professional finish, enhancing the quality of their leather
                    projects.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={bordersImg}
                    alt="A detailed macro closeup of a border leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Borders</h3>{' '}
                  <p>
                    are decorative tools used to create intricate border designs
                    on leather. They add a touch of elegance and sophistication
                    to leather projects, making them stand out.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={camouflageImg}
                    alt="A detailed macro closeup of a camouflage leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Camouflage</h3>{' '}
                  <p>
                    tools are designed to create unique details on leather,
                    allowing artisans to add a distinctive touch to their work.
                    These tools are perfect for creating patterns that mimic
                    natural textures.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={figurecarvingImg}
                    alt="A detailed macro closeup of a figure carving leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Figure Carving</h3>{' '}
                  <p>
                    tools are specialized for creating figures and pictorials on
                    leather. They allow artisans to bring their artistic visions
                    to life, adding depth and character to their projects.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={flowersImg}
                    alt="A detailed macro closeup of a flower leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Flowers</h3>{' '}
                  <p>
                    tools are used to create beautiful floral designs on
                    leather. They are perfect for adding a touch of nature and
                    elegance to leather projects, making them visually appealing
                    and unique.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={geometricsImg}
                    alt="A detailed macro closeup of a geometric leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Geometrics</h3>{' '}
                  <p>
                    tools are designed to create precise geometric patterns on
                    leather. They are ideal for adding structure and symmetry to
                    leather projects, enhancing their overall design.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={mattersImg}
                    alt="A detailed macro closeup of a matter leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Matters</h3>{' '}
                  <p>
                    are essential for smoothing and covering up beveler marks,
                    uneven tooling and small mistakes. They blend the leather
                    into a clean, even finish, adding depth to any design.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={mulefootsImg}
                    alt="A detailed macro closeup of a mulefoot leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Mulefoots</h3>{' '}
                  <p>
                    are specialized tools used for creating unique patterns and
                    depth on leather. They are perfect for adding a distinctive
                    touch to leather projects, enhancing their visual appeal.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={pearshadersImg}
                    alt="A detailed macro closeup of a pear shader leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Pear Shaders</h3>{' '}
                  <p>
                    are essential tools for adding shading and depth to leather
                    projects. They allow artisans to create realistic effects,
                    enhancing the overall aesthetic of their work.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={seedersImg}
                    alt="A detailed macro closeup of a seeder leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Seeders</h3>{' '}
                  <p>
                    are versatile tools used to create intricate patterns and
                    textures on leather often used in floral carving. They are
                    perfect for adding detail and interest to various leather
                    projects, enhancing their visual appeal.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={veinersImg}
                    alt="A detailed macro closeup of a veiner leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Veiners</h3>{' '}
                  <p>
                    are specialized tools used to create detailed veins and
                    textures on leather. They are essential for adding realism
                    and depth to leather projects, enhancing their overall
                    design.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={modelingspoonsImg}
                    alt="A detailed macro closeup of a modeling spoon leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Modeling Spoons</h3>{' '}
                  <p>
                    are versatile tools used for shaping and smoothing leather.
                    They are perfect for adding fine details and finishing
                    touches to leather projects, enhancing their overall
                    quality.
                  </p>
                </div>
              </article>
            </Grid.Column>
            <Grid.Column
              largeScreen={4}
              widescreen={4}
              computer={6}
              tablet={6}
              mobile={12}
            >
              <article className="tool-card">
                <div className="image-wrapper">
                  <Image
                    src={swivelknivesImg}
                    alt="A detailed macro closeup of a swivel knife leatherworking tool with a smooth leather background."
                    className="rounded"
                    loading="lazy"
                  />
                </div>
                <div className="tool-text">
                  <h3 className="highlight">Swivel Knives</h3>{' '}
                  <p>
                    are essential tools for carving intricate designs on
                    leather. They allow artisans to create precise cuts and
                    patterns, enhancing the overall aesthetic of their leather
                    projects.
                  </p>
                </div>
              </article>
            </Grid.Column>
          </Grid>
        </Container>
      </section>

      <section className="full-width section" id="ourteam">
        <Container>
          <h2 className="animation">Our Team</h2>
          <div className="header-line animation" />
          <p className="animation">
            We are a team of passionate artisans and craftsmen dedicated to
            creating tools that not only meet the highest standards of quality
            but also embody the spirit of craftsmanship.
          </p>
          <br />
          <br />
          <Grid stackable stretched columns={2} className="animation">
            <Grid.Column largeScreen={6} computer={6} tablet={12}>
              <div className="scale-wrapper rounded">
                <Image
                  src={bobImg}
                  alt="Robert Beard working on leatherworking tools at the sanding machine."
                  className="scale"
                  loading="lazy"
                />
              </div>
              <div>
                <h3 className="highlight">Robert Beard</h3>{' '}
                <p>
                  is an Al Stohlman Award winner and master craftsman with over
                  50 years of experience in tool making. His expertise lies in
                  creating tools that are not only functional but also
                  aesthetically pleasing. Robert's passion for craftsmanship is
                  evident in every piece he creates.
                </p>
              </div>
            </Grid.Column>
            <Grid.Column largeScreen={6} computer={6} tablet={12}>
              <div className="scale-wrapper rounded">
                <Image
                  src={daanImg}
                  alt="Daniëlle Roossien polishing leatherworking tools at a buffing machine."
                  className="scale"
                  loading="lazy"
                />
              </div>
              <div>
                <h3 className="highlight">Daniëlle Roossien</h3>{' '}
                <p>
                  is a skilled artisan specializing in tool design and creation.
                  Drawing on her award-winning background at the University of
                  Lincoln, Daniëlle brings attention to detail to every piece,
                  ensuring each tool is both exceptional in performance and a
                  true work of art.
                </p>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </section>

      <section className="full-width section" id="contact">
        <Container>
          <h2 className="animation">Contact Us</h2>
          <div className="header-line animation" />
          <p className="animation">
            Although we are always behind on orders, we love to talk to you and
            answer any questions you may have about our tools or your projects.
            Feel free to reach out to us to order, to ask for more information,
            or just to share your passion for leatherworking.
          </p>
          <br />
          <br />
          <br />
          <br />
        </Container>
        <div className="cover-container">
          <Image
            src={contactusImg}
            alt="Robert Beard and Daniëlle Roossien posing together inside their workshop."
            className="cover"
            loading="lazy"
          />
          <div className="cover-overlay-light" />
          <div className="cover-text">
            <Container>
              <p className="cover-title animation">We Love to Talk to You!</p>
              <div className="header-line animation" />
              <p className="animation">
                Give us a call at{' '}
                <a href="tel:+15056327039">+ 1 (505) 632-7039</a>, send us an{' '}
                <a href="mailto:info@proseriesleathertools.com">e-mail</a> or
                meet us at one of the many tradeshows we attend. We are always
                happy to discuss our tools and how they can help you with your
                leatherworking projects.
              </p>
            </Container>
          </div>
        </div>
      </section>
    </div>
  );
};

SiteView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

SiteView.defaultProps = {
  content: null,
};

export default SiteView;
